import { createSlice } from '@reduxjs/toolkit';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import vehicleService from '../services/vehicleService';
import { Vehicle } from './models/vehicle.model';
import { PagedList } from './models/paged-list.model';
import { SortModel } from './models/sort-model.model';
import { VehicleDocument } from './models/document.model';

interface VehicleState {
  selectedFilter: string;
  searchFilter: string;
  page: number;
  pageSize: number;
  sortModel: SortModel | null;
  loadingVehicleList: boolean;
  loadedVehicleList: boolean;
  vehicleList: PagedList<Vehicle>;

  loadingVehicle: boolean;
  loadedVehicle: boolean;
  savingVehicle: boolean;
  savedVehicle: boolean;
  savingError: string | null;
  vehicle: Vehicle | null;
  vehicleDocuments: VehicleDocument[];
  uploadingDocument: boolean;
  uploadedDocument: boolean;
  uploadError: string | null;

  importingVehicles: boolean;
  importedVehicles: boolean;
  importVehiclesError: string | null;
  importVehiclesSuccess: string | null;
}

export const initialState: VehicleState = {
  selectedFilter: ' ',
  searchFilter: '',
  page: 1,
  pageSize: 10,
  sortModel: null,
  loadingVehicleList: false,
  loadedVehicleList: false,
  vehicleList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },

  loadingVehicle: false,
  loadedVehicle: false,
  savingVehicle: false,
  savedVehicle: false,
  savingError: null,
  vehicle: null,
  vehicleDocuments: [],
  uploadingDocument: false,
  uploadedDocument: false,
  uploadError: null,

  importingVehicles: false,
  importedVehicles: false,
  importVehiclesError: null,
  importVehiclesSuccess: null,
};

export const slice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    setSelectedFilter: (state: VehicleState, action: { payload: string }) => {
      state.selectedFilter = action.payload;
      state.page = 1;
    },
    setSearchFilter: (state: VehicleState, action: { payload: string }) => {
      state.searchFilter = action.payload;
      state.page = 1;
    },
    setPage: (state: VehicleState, action: { payload: number }) => {
      state.page = action.payload;
    },
    setPageSize: (state: VehicleState, action: { payload: number }) => {
      state.pageSize = action.payload;
    },
    setSortModel: (
      state: VehicleState,
      action: { payload: SortModel | null },
    ) => {
      state.sortModel = action.payload;
    },
    setLoadingVehicleList: (
      state: VehicleState,
      action: { payload: boolean },
    ) => {
      state.loadingVehicleList = action.payload;
    },
    setLoadedVehicleList: (
      state: VehicleState,
      action: { payload: boolean },
    ) => {
      state.loadedVehicleList = action.payload;
    },
    setVehicleList: (
      state: VehicleState,
      action: { payload: PagedList<Vehicle> },
    ) => {
      state.loadingVehicleList = false;
      state.loadedVehicleList = true;
      state.vehicleList = action.payload;
    },

    setLoadingVehicle: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingVehicle = action.payload;
    },
    setLoadedVehicle: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedVehicle = action.payload;
    },

    setSavingVehicle: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingVehicle = action.payload;
    },
    setSavedVehicle: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedVehicle = action.payload;
    },
    setSavingError: (
      state: VehicleState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingError = action.payload;
    },
    resetSavingFlags: (state: VehicleState, action: { type: string }) => {
      state.savingVehicle = false;
      state.savedVehicle = false;
      state.savingError = null;
    },
    setVehicle: (
      state: VehicleState,
      action: { type: string; payload: Vehicle | null },
    ) => {
      state.loadingVehicle = !action.payload;
      state.loadedVehicle = !!action.payload;
      state.vehicle = action.payload;
    },
    addVehicle: (
      state: VehicleState,
      action: { type: string; payload: Vehicle | null },
    ) => {
      state.loadingVehicle = false;
      state.loadedVehicle = !!action.payload;
      state.vehicle = action.payload;
      if (null != state.vehicle) {
        state.vehicleList = {
          ...state.vehicleList,
          itemCount: state.vehicleList.itemCount + 1,
          items: [state.vehicle, ...state.vehicleList.items],
        };
      }
    },
    updateVehicle: (
      state: VehicleState,
      action: { type: string; payload: Vehicle | null },
    ) => {
      state.loadingVehicle = false;
      state.loadedVehicle = !!action.payload;
      state.vehicle = action.payload;
      if (null != state.vehicle) {
        const vehicles = [...state.vehicleList.items];
        const idx = vehicles.findIndex(
          (v) => v.vehicleID === state.vehicle?.vehicleID,
        );
        vehicles.splice(idx, 1, state.vehicle);
        state.vehicleList = {
          ...state.vehicleList,
          items: vehicles,
        };
      }
    },
    setVehicleDocuments: (
      state: VehicleState,
      action: { type: string; payload: VehicleDocument[] },
    ) => {
      state.vehicleDocuments = action.payload;
    },
    setUploadingDocument: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.uploadingDocument = action.payload;
    },
    setUploadedDocument: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.uploadedDocument = action.payload;
    },
    setUploadError: (
      state: VehicleState,
      action: { type: string; payload: string | null },
    ) => {
      state.uploadError = action.payload;
    },
    addVehicleDocument: (
      state: VehicleState,
      action: { type: string; payload: VehicleDocument },
    ) => {
      state.vehicleDocuments = [...state.vehicleDocuments, action.payload];
    },
    updateVehicleDocument: (
      state: VehicleState,
      action: { type: string; payload: VehicleDocument },
    ) => {
      const docs = [...state.vehicleDocuments];
      const idx = docs.findIndex(
        (d) => d.vehicleDocumentID === action.payload.vehicleDocumentID,
      );
      if (idx >= 0) {
        docs.splice(idx, 1, action.payload);
        state.vehicleDocuments = docs;
      }
    },
    deleteVehicleDocument: (
      state: VehicleState,
      action: { type: string; payload: number },
    ) => {
      const docs = [...state.vehicleDocuments];
      const idx = docs.findIndex((d) => d.vehicleDocumentID === action.payload);
      if (idx >= 0) {
        docs.splice(idx, 1);
        state.vehicleDocuments = docs;
      }
    },

    setImportingVehicles: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.importingVehicles = action.payload;
    },
    setImportedVehicles: (
      state: VehicleState,
      action: { type: string; payload: boolean },
    ) => {
      state.importedVehicles = action.payload;
    },
    setImportVehiclesError: (
      state: VehicleState,
      action: { type: string; payload: string | null },
    ) => {
      state.importVehiclesError = action.payload;
    },
    setImportVehiclesSuccess: (
      state: VehicleState,
      action: { type: string; payload: string | null },
    ) => {
      state.importVehiclesSuccess = action.payload;
    },
    resetImportFlags: (state: VehicleState, action: { type: string }) => {
      state.importingVehicles = false;
      state.importedVehicles = false;
      state.importVehiclesError = null;
      state.importVehiclesSuccess = null;
    },
  },
});

export const { setSelectedFilter } = slice.actions;
export const { setSearchFilter } = slice.actions;
export const { setPage } = slice.actions;
export const { setPageSize } = slice.actions;
export const { setSortModel } = slice.actions;
export const { setLoadingVehicleList } = slice.actions;
export const { setLoadedVehicleList } = slice.actions;
export const { setVehicleList } = slice.actions;

export const { setLoadingVehicle } = slice.actions;
export const { setLoadedVehicle } = slice.actions;
export const { setSavingVehicle } = slice.actions;
export const { setSavedVehicle } = slice.actions;
export const { setSavingError } = slice.actions;
export const { resetSavingFlags } = slice.actions;
export const { setVehicle } = slice.actions;
export const { addVehicle } = slice.actions;
export const { updateVehicle } = slice.actions;
export const { setVehicleDocuments } = slice.actions;
export const { addVehicleDocument } = slice.actions;
export const { updateVehicleDocument } = slice.actions;
export const { deleteVehicleDocument } = slice.actions;
export const { setUploadingDocument } = slice.actions;
export const { setUploadedDocument } = slice.actions;
export const { setUploadError } = slice.actions;
export const { setImportingVehicles } = slice.actions;
export const { setImportedVehicles } = slice.actions;
export const { setImportVehiclesError } = slice.actions;
export const { setImportVehiclesSuccess } = slice.actions;
export const { resetImportFlags } = slice.actions;

export const fetchVehicleListAsync =
  (
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    providerId?: number,
  ) =>
  (dispatch: Function) => {
    dispatch(setLoadingVehicleList(true));
    dispatch(setLoadedVehicleList(false));
    vehicleService
      .findVehicles(pageSize, page, filter, search, sortModel, providerId)
      .subscribe((vehicleList) => {
        dispatch(setVehicleList(vehicleList));
      });
  };

export const refetchVehicleListAsync =
  (providerId: number | undefined) =>
  (dispatch: Function, getState: Function) => {
    const state = getState();
    const vehicleState = state.vehicle;
    const providerState = state.provider;
    const selectedFilter = providerId
      ? providerState.vehicleSelectedFilter
      : vehicleState.selectedFilter;
    const searchFilter = providerId
      ? providerState.vehicleSearchFilter
      : vehicleState.searchFilter;
    dispatch(
      fetchVehicleListAsync(
        vehicleState.pageSize,
        vehicleState.page,
        selectedFilter,
        searchFilter,
        vehicleState.sortModel,
        providerId,
      ),
    );
  };

export const getVehicleAsync = (id: number) => (dispatch: Function) => {
  dispatch(setVehicle(null));
  vehicleService.getVehicle(id).subscribe((vehicle) => {
    dispatch(setVehicle(vehicle));
  });
};

const handleSaveVehicleSuccess = (
  dispatch: Function,
  vehicle: Vehicle,
  isNew: boolean,
) => {
  dispatch(isNew ? addVehicle(vehicle) : updateVehicle(vehicle));
  dispatch(setSavingVehicle(false));
  dispatch(setSavedVehicle(true));
};

const handleSaveVehicleError = (dispatch: Function, error: any) => {
  dispatch(setSavingVehicle(false));
  dispatch(setSavedVehicle(false));
  // This should be localized from messageId
  dispatch(setSavingError(error.message));
};

export const createVehicleAsync =
  (vehicle: Vehicle) => (dispatch: Function) => {
    dispatch(setVehicle(null));
    dispatch(setSavingVehicle(true));
    dispatch(setSavedVehicle(false));
    dispatch(setSavingError(null));
    vehicleService
      .createVehicle(vehicle)
      .pipe(
        catchError((err) => {
          handleSaveVehicleError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((createdVehicle) => {
        handleSaveVehicleSuccess(dispatch, createdVehicle, true);
      });
  };

export const updateVehicleAsync =
  (vehicle: Vehicle) => (dispatch: Function) => {
    dispatch(setSavingVehicle(true));
    dispatch(setSavedVehicle(false));
    dispatch(setSavingError(null));
    vehicleService
      .updateVehicle(vehicle)
      .pipe(
        catchError((err) => {
          handleSaveVehicleError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedVehicle) => {
        handleSaveVehicleSuccess(dispatch, updatedVehicle, false);
      });
  };

export const deleteVehicleAsync =
  (providerId: number | undefined, vehicleId: number) =>
  (dispatch: Function) => {
    vehicleService
      .deleteVehicle(vehicleId)
      .pipe(
        catchError((err) => {
          // This should be localized from messageId
          dispatch(setSavingError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(refetchVehicleListAsync(providerId));
      });
  };

export const uploadVehicleDocumentAsync =
  (vehicle: number, file: any, document: VehicleDocument) =>
  (dispatch: Function) => {
    dispatch(setUploadingDocument(true));
    dispatch(setUploadedDocument(false));
    dispatch(setUploadError(null));
    vehicleService.uploadVehicleDocument(vehicle, document, file).subscribe({
      next: (doc) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(true));
        dispatch(addVehicleDocument(doc));
      },
      error: (err) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(false));
        dispatch(setUploadError('common.documentTooLarge'));
      },
    });
  };

export const updateVehicleDocumentAsync =
  (providerID: number, document: VehicleDocument) => (dispatch: Function) => {
    dispatch(setUploadingDocument(true));
    dispatch(setUploadedDocument(false));
    dispatch(setUploadError(null));
    vehicleService
      .updateVehicleDocument(providerID, document)
      .pipe(
        catchError((err) => {
          dispatch(setUploadingDocument(false));
          dispatch(setUploadedDocument(false));
          dispatch(setUploadError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((doc) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(true));
        dispatch(updateVehicleDocument(doc));
      });
  };

export const deleteVehicleDocumentAsync =
  (providerId: number, documentId: number) => (dispatch: Function) => {
    vehicleService
      .deleteVehicleDocument(providerId, documentId)
      .pipe(
        catchError((err) => {
          dispatch(setUploadError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(deleteVehicleDocument(documentId));
      });
  };

export const fetchVehicleDocumentsAsync =
  (vehicleId: number) => (dispatch: Function) => {
    vehicleService
      .getVehicleDocuments(vehicleId)
      // .pipe(
      //   catchError((err) => {
      //     // This should be localized from messageId
      //     dispatch(setSavingError(err.message));
      //     return throwError(() => new Error(err.message));
      //   }),
      // )
      .subscribe((documents) => {
        dispatch(setVehicleDocuments(documents));
      });
  };

export const importVehiclesAsync =
  (providerId: number, file: File) => (dispatch: Function) => {
    dispatch(setImportingVehicles(true));
    dispatch(setImportedVehicles(false));
    dispatch(setImportVehiclesError(null));
    vehicleService
      .importVehicles(providerId, file)
      .pipe(
        catchError((err) => {
          dispatch(setImportingVehicles(false));
          // This should be localized from messageId
          dispatch(
            setImportVehiclesError(err.message || 'Vehicle import failed'),
          );
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(setImportingVehicles(false));
        dispatch(setImportedVehicles(true));
        dispatch(setImportVehiclesSuccess('Vehicle import succeeded'));
      });
  };

export const selectSelectedFilter = (state: { vehicle: VehicleState }) =>
  state.vehicle.selectedFilter;
export const selectSearchFilter = (state: { vehicle: VehicleState }) =>
  state.vehicle.searchFilter;
export const selectPage = (state: { vehicle: VehicleState }) =>
  state.vehicle.page;
export const selectPageSize = (state: { vehicle: VehicleState }) =>
  state.vehicle.pageSize;
export const selectSortModel = (state: { vehicle: VehicleState }) =>
  state.vehicle.sortModel;
export const selectLoadingVehicleList = (state: { vehicle: VehicleState }) =>
  state.vehicle.loadingVehicleList;
export const selectLoadedVehicleList = (state: { vehicle: VehicleState }) =>
  state.vehicle.loadedVehicleList;
export const selectVehicleList = (state: { vehicle: VehicleState }) =>
  state.vehicle.vehicleList;

export const selectLoadingVehicle = (state: { vehicle: VehicleState }) =>
  state.vehicle.loadingVehicle;
export const selectLoadedVehicle = (state: { vehicle: VehicleState }) =>
  state.vehicle.loadedVehicle;
export const selectSavingVehicle = (state: { vehicle: VehicleState }) =>
  state.vehicle.savingVehicle;
export const selectSavedVehicle = (state: { vehicle: VehicleState }) =>
  state.vehicle.savedVehicle;
export const selectSavingError = (state: { vehicle: VehicleState }) =>
  state.vehicle.savingError;
export const selectVehicle = (state: { vehicle: VehicleState }) =>
  state.vehicle.vehicle;
export const selectVehicleDocuments = (state: { vehicle: VehicleState }) =>
  state.vehicle.vehicleDocuments;
export const selectUploadingDocument = (state: { vehicle: VehicleState }) =>
  state.vehicle.uploadingDocument;
export const selectUploadedDocument = (state: { vehicle: VehicleState }) =>
  state.vehicle.uploadedDocument;
export const selectUploadError = (state: { vehicle: VehicleState }) =>
  state.vehicle.uploadError;
export const selectImportingVehicles = (state: { vehicle: VehicleState }) =>
  state.vehicle.importingVehicles;
export const selectImportedVehicles = (state: { vehicle: VehicleState }) =>
  state.vehicle.importedVehicles;
export const selectImportVehiclesError = (state: { vehicle: VehicleState }) =>
  state.vehicle.importVehiclesError;
export const selectImportVehiclesSuccess = (state: { vehicle: VehicleState }) =>
  state.vehicle.importVehiclesSuccess;

export default slice.reducer;
